import React, { useEffect, useState } from "react";
import { SuperAdminShell } from "../components/SuperAdminShell";
import { useSuperAdminTenant } from "../context/SuperAdminTenantContext";
import saApi from "../lib/saApi";
import { toast } from "sonner";
import { Gauge, Database, Building2, Flame, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SAPerformance() {
  const { tenantId } = useSuperAdminTenant();
  const [hours, setHours] = useState("24");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warming, setWarming] = useState(false);
  const [lastWarm, setLastWarm] = useState(null);

  const load = () => {
    setLoading(true);
    const params = { hours: Number(hours) };
    if (tenantId && tenantId !== "all") params.tenant_id = tenantId;
    saApi.get("/performance", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData({ total: 0, avg_ms: 0, p95_ms: 0, cache_hit_rate: 0, slowest_tenants: [], slowest_datasets: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [tenantId, hours]); // eslint-disable-line react-hooks/exhaustive-deps

  const warmNow = async () => {
    setWarming(true);
    try {
      const { data } = await saApi.post("/performance/warm-now");
      setLastWarm(data);
      toast.success(`Cache warm triggered: ${data.last_result || "done"}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setWarming(false); }
  };

  return (
    <SuperAdminShell
      title="Performance"
      actions={
        <Button data-testid="sa-perf-warm-now" onClick={warmNow} disabled={warming}
                className="text-white" style={{ background: "linear-gradient(135deg,#22D3EE,#A78BFA)" }}>
          {warming ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Flame className="w-4 h-4 mr-1.5" />}
          Warm cache (all tenants)
        </Button>
      }
    >
      <div className="px-5 md:px-8 py-6 space-y-6">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={hours} onValueChange={setHours}>
            <SelectTrigger data-testid="sa-perf-window" className="w-[160px] bg-white/5 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="24">Last 24 hours</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
          {lastWarm?.finished_at && (
            <span className="text-[11px] text-white/40">Last warm: {new Date(lastWarm.finished_at).toLocaleString()} · {lastWarm.last_result}</span>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Queries" value={(data?.total ?? "—").toLocaleString?.() ?? data?.total} icon={Gauge} accent="#22D3EE" />
          <Kpi label="Avg latency" value={data ? `${data.avg_ms} ms` : "—"} icon={Gauge} accent="#FBBF24" />
          <Kpi label="p95 latency" value={data ? `${data.p95_ms} ms` : "—"} icon={Gauge} accent="#A78BFA" />
          <Kpi label="Cache hit rate" value={data ? `${data.cache_hit_rate}%` : "—"} icon={Database} accent="#34D399" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Panel title="Slowest tenants" icon={Building2}>
            <table className="w-full text-xs" data-testid="sa-perf-tenants-table">
              <thead><tr className="text-left text-white/40 border-b border-white/10"><th className="py-1.5">Tenant</th><th className="py-1.5 text-right">Queries</th><th className="py-1.5 text-right">Avg</th><th className="py-1.5 text-right">Max</th></tr></thead>
              <tbody>
                {(data?.slowest_tenants || []).map((t) => (
                  <tr key={t.tenant_id} className="border-b border-white/5 last:border-0">
                    <td className="py-1.5 text-white/90 truncate max-w-[180px]">{t.tenant_name}</td>
                    <td className="py-1.5 text-right tabular-nums text-white/70">{t.count}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold text-cyan-300">{t.avg_ms} ms</td>
                    <td className="py-1.5 text-right tabular-nums text-white/50">{t.max_ms} ms</td>
                  </tr>
                ))}
                {!(data?.slowest_tenants || []).length && <tr><td colSpan={4} className="py-5 text-center text-white/40">No queries in window.</td></tr>}
              </tbody>
            </table>
          </Panel>
          <Panel title="Slowest datasets" icon={Database}>
            <table className="w-full text-xs" data-testid="sa-perf-datasets-table">
              <thead><tr className="text-left text-white/40 border-b border-white/10"><th className="py-1.5">Dataset</th><th className="py-1.5">Tenant</th><th className="py-1.5 text-right">Avg</th><th className="py-1.5 text-right">Max</th></tr></thead>
              <tbody>
                {(data?.slowest_datasets || []).map((d) => (
                  <tr key={d.dataset_id} className="border-b border-white/5 last:border-0">
                    <td className="py-1.5 text-white/90 truncate max-w-[140px]">{d.dataset_name}</td>
                    <td className="py-1.5 text-white/50 truncate max-w-[100px]">{d.tenant_name}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold text-cyan-300">{d.avg_ms} ms</td>
                    <td className="py-1.5 text-right tabular-nums text-white/50">{d.max_ms} ms</td>
                  </tr>
                ))}
                {!(data?.slowest_datasets || []).length && <tr><td colSpan={4} className="py-5 text-center text-white/40">No queries in window.</td></tr>}
              </tbody>
            </table>
          </Panel>
        </div>
        <p className="text-[11px] text-white/40">Per-tenant slow-query drill-down and manual cache warm for a single tenant remain in that tenant&apos;s Admin Console → Performance.</p>
      </div>
    </SuperAdminShell>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-2xl p-5 border border-white/10 backdrop-blur-md"
         style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))" }}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-white/50">{label}</div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${accent}22` }}>
          <Icon className="w-4 h-4" style={{ color: accent }} />
        </div>
      </div>
      <div className="mt-3 text-2xl font-display text-white tabular-nums">{value}</div>
    </div>
  );
}

function Panel({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl p-4 border border-white/10 backdrop-blur-md" style={{ background: "rgba(255,255,255,0.02)" }}>
      <h4 className="text-xs font-semibold text-white mb-2 flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-cyan-300" />{title}</h4>
      {children}
    </div>
  );
}
